import { computed, Injectable, Signal } from "@angular/core";
import { CardConfig } from "../models/card-model";
import { CardStateService } from "../states/card-state.service";
import { DataService } from "./data.service";
import { CardError, CardErrorCode, CardErrorSeverity } from "../models/card-model";

/**
 * Service for managing analytics card configurations and data.
 *
 * This service is the central point for all card-related operations in the analytics module.
 * It handles the creation, retrieval, updating, and deletion of card configurations,
 * as well as managing the state of cards across the application.
 *
 * Provides:
 * - CRUD operations for card configurations
 * - Card state management via Angular signals
 * - Configuration validation and normalization
 * - Mock data generation for development and testing
 *
 * Supported card types:
 * - Chart cards (line, bar, etc.)
 * - HTML cards for custom content
 * - Markdown cards for documentation
 *
 */
@Injectable({
  providedIn: "root",
})
export class CardService {
  /**
   * Cache of computed signals for card configs.
   * This prevents creating a new computed signal on every cardConfig() call,
   * which was causing excessive effect re-runs and log spam.
   */
  private cardConfigSignals = new Map<string, Signal<CardConfig | undefined>>();

  constructor(
    private cardState: CardStateService,
    private dataService: DataService
  ) {}

  get allCards() {
    return this.cardState.allCards;
  }

  /**
   * Gets a cached computed signal for a card's configuration.
   * The signal is created once per card ID and reused for subsequent calls.
   * This is critical for performance - creating new computed signals on each
   * call causes effects to re-subscribe and re-run unnecessarily.
   */
  cardConfig(cardId: string): Signal<CardConfig | undefined> {
    // Return cached signal if it exists
    let signal = this.cardConfigSignals.get(cardId);
    if (signal) {
      return signal;
    }

    // Create and cache a new computed signal for this card
    signal = computed(() => this.cardState.getCard(cardId));
    this.cardConfigSignals.set(cardId, signal);
    return signal;
  }

  /**
   * Stores a new card configuration or replaces an existing one.
   *
   * @param id Unique identifier for the card
   * @param config Complete card configuration to store
   */
  setCardConfig(id: string, config: CardConfig) {
    console.log(`[CardService] Setting config for card ${id}`);
    this.cardState.addCard(id, config);
  }

  /**
   * Removes a card configuration from the service.
   *
   * @param id Unique identifier for the card to delete
   */
  deleteCard(id: string) {
    this.cardState.deleteCard(id);
  }

  /**
   * Updates an existing card configuration with partial data.
   * Filters out empty/null values to preserve existing good data.
   *
   * @param id Unique identifier for the card
   * @param updates Partial configuration updates to apply
   */
  updateCardConfig(id: string, updates: Partial<CardConfig>) {
    this.cardState.updateCard(id, updates);
  }

  /**
   * Gets card configurations synchronously for the given card IDs.
   * This is a synchronous version of getCardConfigs that returns the configurations directly.
   *
   * @param cardIds Array of card identifiers to retrieve
   * @returns Array of card configurations
   */
  getCardConfigsSync(cardIds: string[]): CardConfig[] {
    return cardIds
      .map((id) => {
        const card = this.cardState.getCard(id);
        if (card) {
          return card;
        }
        console.log(`[CardService] No config found for ${id}`);
        return null;
      })
      .filter(Boolean) as CardConfig[];
  }

  /**
   * Gets a signal for card data associated with a specific card ID.
   * @deprecated Use cardConfig(cardId) instead - this method is redundant.
   *
   * @param cardId The ID of the card to get data for
   * @returns A signal with the card data
   */
  getCardDataSignal(cardId: string): Signal<CardConfig | undefined> {
    return this.cardState.getCardSignal(cardId);
  }

  /**
   * Clear all card configurations and reset signal cache
   */
  clearAllCards(): void {
    this.cardConfigSignals.clear();
    this.cardState.clearAllCards();
  }

  /**
   * Sets an error state for a card.
   * Use this when data fetching, processing, or rendering fails.
   *
   * @param cardId The ID of the card that encountered an error
   * @param error The error information
   */
  setCardError(cardId: string, error: CardError): void {
    console.error(`[CardService] Setting error for card ${cardId}:`, error);
    this.cardState.setCardError(cardId, error);
  }

  /**
   * Clears the error state for a card.
   * Call this when retrying or when the error condition is resolved.
   *
   * @param cardId The ID of the card to clear the error for
   */
  clearCardError(cardId: string): void {
    console.log(`[CardService] Clearing error for card ${cardId}`);
    this.cardState.clearCardError(cardId);
  }

  /**
   * Gets the current error state for a card
   *
   * @param cardId The ID of the card
   * @returns The card error or undefined if no error
   */
  getCardError(cardId: string): CardError | undefined {
    return this.cardState.getCardError(cardId);
  }

  /**
   * Gets a signal for a card's error state
   *
   * @param cardId The ID of the card
   * @returns A signal with the card error or undefined
   */
  getCardErrorSignal(cardId: string): Signal<CardError | undefined> {
    return computed(() => this.cardState.getCard(cardId)?.error);
  }

  /**
   * Creates a structured CardError object.
   * Helper method to create consistent error objects.
   *
   * @param code Error code for categorization
   * @param message User-friendly error message
   * @param options Additional error configuration
   * @returns CardError object
   */
  createError(
    code: CardErrorCode,
    message: string,
    options?: {
      severity?: CardErrorSeverity;
      details?: string;
      originalError?: unknown;
      requestId?: string;
      executionId?: string;
      context?: Record<string, unknown>;
      recoverable?: boolean;
      suggestedAction?: string;
    }
  ): CardError {
    return this.dataService.createCardError(code, message, options);
  }
}
